/**
 * @architecture-header 1
 * @layer routes
 * @domain settings
 * @responsibility Render the student-only Settings shell: a thin, self-contained page (its own
 *   header and back navigation, not integrated with the main workspace TopBar/Rail) hosting the
 *   SET-F10 student settings pane. Parallel to, and strictly separate from, the teacher-only
 *   SettingsShell -- there is no shared tab list because there is exactly one student settings
 *   surface, not several named tabs.
 * @state-owner none; the lane-owned pane owns its own server state
 * @allowed-imports React; shared translator state; the settings-student module's public API
 * @forbidden direct API calls or mutations in this file; teacher settings; SettingsShell internals
 * @public-api WorkspaceShell
 * @side-effects invokes the caller's back-navigation callback
 * @tests StudentSettingsShell.test.tsx
 */

import { StudentSettingsPage } from '../../modules/settings-student';
import { useTranslator } from '../../shared/model/use-workspace';

export function StudentSettingsShell({
  onBack,
  surface,
}: {
  onBack: () => void;
  surface: 'desktop' | 'mobile';
}) {
  const t = useTranslator();

  // "Student Settings" (plain, untranslated landmark label) is deliberately distinct from the
  // inner pane's own aria-label={t('設定')} -- SettingsShell's own "Settings" wrapper does the
  // same, so getByRole('region', { name: '設定' }) finds exactly one element, the real pane.
  if (surface === 'mobile') {
    return (
      <section
        aria-label="Student Settings"
        className="student-settings-shell student-settings-shell--mobile"
      >
        <header className="student-settings-shell__mobile-header">
          <button aria-label={t('返回')} type="button" onClick={onBack}>
            <svg aria-hidden="true" viewBox="0 0 24 24">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
          <h1>{t('設定')}</h1>
          <span aria-hidden="true" />
        </header>
        <StudentSettingsPage mobile />
      </section>
    );
  }

  return (
    <main
      aria-label="Student Settings"
      className="student-settings-shell student-settings-shell--desktop"
    >
      <header className="student-settings-shell__desktop-header">
        <button type="button" onClick={onBack}>
          <svg aria-hidden="true" viewBox="0 0 24 24">
            <path d="m15 18-6-6 6-6" />
          </svg>
          {t('返回')}
        </button>
        <h1 className="sr-only">{t('設定')}</h1>
      </header>
      <StudentSettingsPage mobile={false} />
    </main>
  );
}
